import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRiskFlow } from "../contexts/RiskFlowContext";
import { useDayPlan } from "./useDayPlan";
import { useArbitrumLatest } from "./useArbitrumLatest";
import { useEquitiesSentimentPacket } from "./useEquitiesSentimentPacket";
import { getRuntimeApiBase } from "../lib/runtime-api-base";
import { buildTiltScoutSnapshot } from "../components/narrative/tilt-scout-model";
import { packetToTiltScoutSnapshot } from "../components/narrative/tilt-scout-packet";
import type { TiltScoutInstrumentOutlook } from "../components/narrative/tilt-scout-types";

const OUTLOOK_CACHE_KEY = "fintheon:tiltscout-outlook";
const POLL_MS = 60_000;
const API_BASE = getRuntimeApiBase();

interface OutlookResponse {
  instruments?: TiltScoutInstrumentOutlook[];
}

function endpoint(path: string) {
  return `${API_BASE}${path}`;
}

function loadCachedOutlook(): TiltScoutInstrumentOutlook[] {
  try {
    const raw = localStorage.getItem(OUTLOOK_CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function cacheOutlook(outlook: TiltScoutInstrumentOutlook[]) {
  try {
    if (outlook.length === 0) {
      localStorage.removeItem(OUTLOOK_CACHE_KEY);
      return;
    }
    localStorage.setItem(OUTLOOK_CACHE_KEY, JSON.stringify(outlook));
  } catch {
    /* cache is best-effort */
  }
}

export function useTiltScout(selectedSymbol: string) {
  const { alerts, refresh: refreshRiskFlow, initialLoaded } = useRiskFlow();
  const { data: dayPlan, isLoading: isDayPlanLoading } = useDayPlan();
  const {
    packet,
    refresh: refreshPacket,
    isLoading: isPacketLoading,
    error: packetError,
  } = useEquitiesSentimentPacket(selectedSymbol);
  const {
    verdict,
    isLoading: isArbitrumLoading,
    refresh: refreshArbitrum,
  } = useArbitrumLatest(selectedSymbol);
  const [outlook, setOutlook] =
    useState<TiltScoutInstrumentOutlook[]>(loadCachedOutlook);
  const [isOutlookLoading, setIsOutlookLoading] = useState(
    () => outlook.length === 0,
  );
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchOutlook = useCallback(async () => {
    try {
      setIsOutlookLoading(true);
      const response = await fetch(endpoint("/api/predictions/outlook"), {
        credentials: "include",
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = (await response.json()) as OutlookResponse;
      const instruments = body.instruments ?? [];
      setOutlook(instruments);
      cacheOutlook(instruments);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Outlook fetch failed");
    } finally {
      setIsOutlookLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOutlook();
    pollRef.current = setInterval(() => {
      if (document.visibilityState === "visible") void fetchOutlook();
    }, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchOutlook]);

  const refresh = useCallback(async () => {
    await Promise.all([
      refreshPacket(),
      fetchOutlook(),
      refreshRiskFlow(),
      refreshArbitrum(),
    ]);
  }, [fetchOutlook, refreshArbitrum, refreshPacket, refreshRiskFlow]);

  const snapshot = useMemo(
    () =>
      packet
        ? packetToTiltScoutSnapshot(packet, selectedSymbol)
        : buildTiltScoutSnapshot({
            selectedSymbol,
            outlook,
            alerts,
            dayPlan,
            verdict,
          }),
    [alerts, dayPlan, outlook, packet, selectedSymbol, verdict],
  );

  return {
    snapshot,
    packet,
    refresh,
    isLoading:
      isPacketLoading ||
      isOutlookLoading ||
      isDayPlanLoading ||
      isArbitrumLoading,
    hasInitialData:
      Boolean(packet) ||
      initialLoaded ||
      outlook.length > 0 ||
      Boolean(dayPlan),
    error: packetError ?? error,
  };
}
